import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Row,
  Col,
  Card,
  Label,
  Alert,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { useFetch } from "../../utils/fetchHook";
import { getCookie } from "../../jwt/_helpers/cookie";
import { Redirect } from "react-router-dom";
import TableHelper from "../../components/tableHelper";
import "react-day-picker/lib/style.css";

import Select from "react-select";
import Datetime from "react-datetime";

import EarningsTable from "../../components/earningsTable";
import ViewLayout from "../../components/viewLayout";
import {
  buildAccountingReport,
  getAccountingReportRows,
  getNextAccountingReportId,
  getReleaseById,
  normalizeAccountingReportRow,
} from "../../utils/accountingDemoHelpers";
import {
  markAccountedEarnings as persistAccountedEarnings,
  upsertDemoAccountingReport,
} from "../../utils/demoAccountingSessionStore";
import {
  addAccountingReport,
  markAccountedEarnings,
} from "../../redux/accountingReports/action";

const AddAccounting = () => {
  const dispatch = useDispatch();
  const createdReports = useSelector(
    state => state.accountingReports?.created || [],
  );
  const token = getCookie("token");
  const [tableData, setTableData] = useState([]);

  const [success, setSuccess] = useState(false);
  const [errorPut, setError] = useState(false);
  const [errorMsg, setErrorMsg] = useState(
    "Something went wrong! Please refresh page and try again!",
  );
  const [openModal, setToggleModal] = useState(false);
  const [redirect, setRedirect] = useState(false);
  const [release_ops, setReleaseOps] = useState([]);

  const [startingMonth, setStartingMonth] = useState(
    Datetime.moment().subtract(15, "month"),
  );
  const [endingMonth, setEndingMonth] = useState(
    Datetime.moment().subtract(4, "month"),
  );

  const latestSelectableMonth = Datetime.moment().startOf("month");

  const isNotFutureMonth = current =>
    Datetime.moment(current).isSameOrBefore(latestSelectableMonth, "month");

  const validStartingMonth = current => isNotFutureMonth(current);

  const validRange = selectedEndingMonth => {
    if (!isNotFutureMonth(selectedEndingMonth)) {
      return false;
    }
    const yearAfterStartingMonth = Datetime.moment(startingMonth).add(11, "M");
    return selectedEndingMonth.isBetween(
      startingMonth,
      yearAfterStartingMonth,
      "month",
      "[]",
    );
  };

  const [activeReleaseTab, setActiveReleaseTab] = useState(null);

  const didEff = useRef(false);
  const [forceUpdate, setForce] = useState(1);

  const [openEarnings, error, isloading] = useFetch(
    "GET",
    `earnings/accounting-overview/?release=${activeReleaseTab?.value || "0"}&period_start=${startingMonth.format("MM-YYYY")}&period_end=${endingMonth.format("MM-YYYY")}`,
    token,
    false,
    forceUpdate,
  );

  const handleFilterChange = (event, filter) => {
    switch (filter) {
      case "release":
        setActiveReleaseTab(event);
        setError(false);
        setErrorMsg("");
        break;
      default:
        break;
    }
  };

  const resetFilters = () => {
    setActiveReleaseTab(null);
    setStartingMonth(Datetime.moment().subtract(15, "month"));
    setEndingMonth(Datetime.moment().subtract(4, "month"));
    setError(false);
    setErrorMsg("");
  };

  const handleStartingMonthChange = value => {
    setStartingMonth(value);
    let yearAfterStartingMonth = Datetime.moment(value).add(11, "M");
    if (yearAfterStartingMonth.isAfter(latestSelectableMonth, "month")) {
      yearAfterStartingMonth = latestSelectableMonth.clone();
    }
    setEndingMonth(yearAfterStartingMonth);
  };

  const handleEndingMonthChange = value => {
    setEndingMonth(value);
  };

  useEffect(() => {
    if (!activeReleaseTab?.value) {
      setTableData([]);
    } else if (Array.isArray(openEarnings?.revenue_data)) {
      setTableData(openEarnings.revenue_data);
    }

    if (Array.isArray(openEarnings?.releases)) {
      setReleaseOps(
        openEarnings.releases.map(release => ({
          label: release.name,
          value: String(release.id),
        })),
      );
    }
  }, [openEarnings, activeReleaseTab]);

  useEffect(() => {
    if (!didEff.current) {
      didEff.current = true;
      return;
    }
    setForce(prev => prev + 1);
  }, [activeReleaseTab, startingMonth, endingMonth]);

  const tableProps = {
    tableData,
  };

  const openRows = (openEarnings?.revenue_data || []).filter(
    row => row.status === 0,
  );

  function generateAccountingReport() {
    setError(false);
    setErrorMsg("");

    if (activeReleaseTab?.value) {
      if (openRows.length > 0) {
        setToggleModal(true);
      } else {
        setError(true);
        setErrorMsg("No earnings available for accounting.");
      }
    } else {
      setError(true);
      setErrorMsg("Please select a particular release for accounting");
    }
  }

  const handleConfirmGenerate = () => {
    const selectedRelease = getReleaseById(activeReleaseTab.value);
    const allReports = [
      ...getAccountingReportRows(),
      ...createdReports,
    ];
    const nextId = getNextAccountingReportId(allReports);
    const { report, accountedKeys } = buildAccountingReport({
      releaseId: activeReleaseTab.value,
      releaseName: activeReleaseTab.label,
      releaseArtwork: selectedRelease?.artwork || null,
      periodStart: startingMonth,
      periodEnd: endingMonth,
      revenueRows: openEarnings?.revenue_data || [],
      nextId,
    });

    upsertDemoAccountingReport(normalizeAccountingReportRow(report));
    persistAccountedEarnings(accountedKeys);
    dispatch(addAccountingReport(normalizeAccountingReportRow(report)));
    dispatch(markAccountedEarnings(accountedKeys));

    setSuccess(true);
    setToggleModal(false);
    setForce(prev => prev + 1);
    setTimeout(() => setRedirect(true), 1000);
  };

  return (
    <>
      <Card className="">
        <h5 className="ml-4 mt-4">Filters</h5>
        <Row className="mb-4">
          <Col className="col-lg-6 col-md-6 col-sm-12 col-12">
            <div className="mx-4">
              <Label>Month Range</Label>
              <div className="starting-month">
                <Datetime
                  locale="en-gb"
                  dateFormat="YYYY-MM"
                  timeFormat={false}
                  inputProps={{ placeholder: "Starting Month" }}
                  value={startingMonth}
                  isValidDate={validStartingMonth}
                  onChange={handleStartingMonthChange}
                />
              </div>
              <div className="ending-month">
                <Datetime
                  locale="en-gb"
                  dateFormat="YYYY-MM"
                  timeFormat={false}
                  inputProps={{ placeholder: "Ending Month" }}
                  value={endingMonth}
                  isValidDate={validRange}
                  onChange={handleEndingMonthChange}
                />
              </div>
            </div>
          </Col>

          <Col className="col-lg-6 col-md-6 col-sm-12 col-12">
            <div className="mx-4">
              <Label>Release</Label>
              <Select
                components={{ IndicatorSeparator: () => null }}
                name="release_filter"
                placeholder="Select a release..."
                options={release_ops}
                value={activeReleaseTab}
                onChange={e => handleFilterChange(e, "release")}
              />
            </div>
          </Col>
        </Row>

        <Row>
          <Col className="col-lg-12 col-md-12 col-sm-12 col-12">
            <div className="mx-4 mb-4" style={{ float: "right" }}>
              <Button
                className="btn btn-outline-success"
                style={{ width: "200px", height: "40px", marginRight: "10px" }}
                onClick={() => generateAccountingReport()}
              >
                Generate Accounting Report
              </Button>
              <Button
                className="btn btn-outline-info"
                style={{ width: "160px", height: "40px" }}
                onClick={() => {
                  resetFilters();
                }}
              >
                Reset Filters
              </Button>
            </div>
          </Col>
        </Row>
      </Card>
      {isloading ? (
        <TableHelper loading />
      ) : error ? (
        <TableHelper error />
      ) : (
        <div className="mb-5">
          <div>
            {success && (
              <Alert color="success">Accounting has been created!</Alert>
            )}
            {redirect ? <Redirect to="/accounting" /> : null}
            {errorPut && <Alert color="danger">{errorMsg}</Alert>}
          </div>

          <ViewLayout title={"Earnings available for accounting"}>
            <EarningsTable {...tableProps} />
          </ViewLayout>
        </div>
      )}

      {openModal && activeReleaseTab && (
        <Modal isOpen={openModal} centered>
          <ModalHeader>Add Accounting</ModalHeader>
          <ModalBody>
            {`Are you sure you want to add new accounting for Release: ${activeReleaseTab.label}, PERIOD: ${startingMonth.format("MMM-YYYY")} TO ${endingMonth.format("MMM-YYYY")} ?`}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setToggleModal(false)}>
              Cancel
            </Button>
            <Button color="success" onClick={handleConfirmGenerate}>
              Confirm
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </>
  );
};

export default AddAccounting;
